import React, { useEffect, useRef, useState } from 'react';
import { ANOS_TIMELINE, CATEGORIAS, acaoPrincipal, historicoRecente, nomeVendedor, timelineDoCurso } from './regras';

const COR = { neg: 'var(--cx-neg)', fec: 'var(--cx-fec)', per: 'var(--cx-per)', nm: 'var(--cx-nm)' };

function corDoItem(item) {
  const categoria = item && item.categoria && CATEGORIAS[item.categoria];
  return categoria ? COR[categoria.classe] : COR.nm;
}

// Chip "Antes": turmas até 2024. Cor do status mais recente; o número é quantas turmas.
function ChipHistorico({ curso, onHistorico }) {
  const historico = historicoRecente(curso);
  if (!historico.length) return <span className="cx-ano-chip cx-antes" title="Sem histórico antes de 2025" />;
  const titulo = 'Histórico antes de 2025:\n' + historico
    .map(h => `Turma ${h.Ano_Form}: ${h.CategoriaStatus || h.Status} · ${nomeVendedor(h.vendedor_id, h.nome_vendedor || h.vendedor)}`)
    .join('\n') + '\n\nClique para ver no painel do curso.';
  return (
    <button
      type="button"
      className="cx-ano-chip cx-antes cx-tem"
      style={{ background: corDoItem({ categoria: historico[0].CategoriaStatus }) }}
      title={titulo}
      aria-label={`Histórico antes de 2025: ${historico.length} turma(s)`}
      onClick={(e) => { e.stopPropagation(); onHistorico(); }}
    >
      {historico.length}
    </button>
  );
}

// Um chip por ano: metade esquerda = 1º semestre, direita = 2º. O primeiro chip resume o histórico.
export function Timeline({ curso, onHistorico }) {
  const porAno = timelineDoCurso(curso);
  const anoAtual = new Date().getFullYear();
  return (
    <div className="cx-timeline">
      <ChipHistorico curso={curso} onHistorico={onHistorico} />
      {ANOS_TIMELINE.map(ano => {
        const itens = porAno[ano] || [];
        const sem1 = itens.find(i => i.semestre === '1');
        const sem2 = itens.find(i => i.semestre === '2');
        const semSemestre = itens.find(i => !i.semestre);
        let fundo;
        if (semSemestre && !sem1 && !sem2) fundo = corDoItem(semSemestre);
        else if (itens.length) {
          const c1 = sem1 ? corDoItem(sem1) : 'var(--cx-vazio)';
          const c2 = sem2 ? corDoItem(sem2) : 'var(--cx-vazio)';
          fundo = c1 === c2 ? c1 : `linear-gradient(90deg, ${c1} 50%, ${c2} 50%)`;
        }
        const titulo = itens.length
          ? itens.map(i => `Turma ${i.turma}: ${i.status} · ${i.vendedor || 'A definir'}${i.dataInclusao ? ` · orçamento em ${i.dataInclusao}` : ''}`).join('\n')
          : `${ano}: sem negócio`;
        const classes = ['cx-ano-chip', itens.length ? 'cx-tem' : '', ano === anoAtual ? 'cx-atual' : ''].join(' ');
        return <span key={ano} className={classes} style={fundo ? { background: fundo } : undefined} title={titulo} />;
      })}
    </div>
  );
}

function AcaoPrincipal({ curso, usuario, permissao, solicitacoes, onOrcar, onSolicitar, onAvaliar }) {
  const acao = acaoPrincipal(curso, usuario, solicitacoes, permissao);
  const parar = (fn) => (e) => { e.stopPropagation(); fn(); };

  switch (acao.tipo) {
    case 'avaliar':
      return <button type="button" className="cx-btn cx-aviso" onClick={parar(() => onAvaliar(acao.solicitacao))}>Avaliar solicitação</button>;
    case 'orcar':
      return (
        <React.Fragment>
          <button type="button" className="cx-btn cx-primario" onClick={parar(() => onOrcar(curso))}>Orçar</button>
          {acao.prioridade != null && (
            <span className={`cx-selo ${acao.prioridade <= 2 ? 'cx-aviso' : 'cx-fec'}`}>
              Prioridade · {acao.prioridade < 0 ? 'vencida' : `${acao.prioridade} ${acao.prioridade === 1 ? 'dia' : 'dias'}`}
            </span>
          )}
        </React.Fragment>
      );
    case 'solicitado':
      return <span className="cx-selo cx-aviso">Solicitado · em análise</span>;
    case 'solicitar':
      return <button type="button" className="cx-btn" onClick={parar(() => onSolicitar(curso, null))}>Solicitar liberação</button>;
    case 'analise':
      return <span className="cx-selo cx-aviso">Em análise</span>;
    default:
      return <span className="cx-selo cx-sem">Com {String(acao.vendedor).split(' ')[0]}</span>;
  }
}

function MenuCurso({ curso, permissao, onAbrir }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const escolher = (e, opcoes) => { e.stopPropagation(); setAberto(false); onAbrir(curso, opcoes); };
  return (
    <div className="cx-col-menu" ref={ref}>
      <button type="button" className="cx-icone-btn" aria-label="Mais ações" aria-expanded={aberto} onClick={(e) => { e.stopPropagation(); setAberto(!aberto); }}>⋯</button>
      {aberto && (
        <div className="cx-menu cx-direita" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="cx-item-menu" onClick={(e) => escolher(e, {})}>Ver turmas e histórico</button>
          {permissao !== 'ver' && <button type="button" className="cx-item-menu" onClick={(e) => escolher(e, { relacionar: true })}>Relacionar vendedor</button>}
        </div>
      )}
    </div>
  );
}

export default function TabelaCursos({ cursos, carregando, usuario, permissao, solicitacoes, rodape, onAbrir, onOrcar, onSolicitar, onAvaliar, onLimparFiltros }) {
  const semTimeline = permissao === 'ver';
  const anoAtual = new Date().getFullYear();

  return (
    <div className={`cx-tabela${semTimeline ? ' cx-sem-timeline' : ''}`} role="table" aria-label="Cursos">
      <div className="cx-linha cx-topo" role="row">
        <span className="cx-col-curso">Curso</span>
        <span className="cx-col-sit">Situação</span>
        <span className="cx-col-vend">Vendedor</span>
        <span className="cx-col-timeline cx-anos-topo">
          <span title="Turmas até 2024">Antes</span>
          {ANOS_TIMELINE.map(a => <span key={a} className={a === anoAtual ? 'cx-atual' : ''}>{a}</span>)}
        </span>
        <span className="cx-col-acao">Próxima ação</span>
        <span className="cx-col-menu" />
      </div>

      {carregando && <div className="cx-vazio-lista">Carregando cursos...</div>}
      {!carregando && cursos.length === 0 && (
        <div className="cx-vazio-lista">
          Nenhum curso encontrado com esses filtros. <button type="button" className="cx-link" onClick={onLimparFiltros}>Limpar filtros</button>
        </div>
      )}

      {!carregando && cursos.map((curso, indice) => {
        const vendedor = nomeVendedor(curso.vendedor_id, curso.nome_vendedor);
        const abrir = () => onAbrir(curso, {});
        return (
          <div
            key={curso.id}
            className={`cx-linha cx-curso${curso.vinculado ? ' cx-vinc' : ''}${indice % 2 ? ' cx-par' : ''}`}
            role="row"
            tabIndex={0}
            onClick={abrir}
            onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); abrir(); } }}
          >
            <div className="cx-col-curso" style={{ minWidth: 0 }}>
              <div className="cx-nome-curso">{curso.nomecurso}</div>
              <div className="cx-sub" title={`${curso.nomeintituicao} (${curso.sigla_instituicao}) · ${curso.nomecidade}/${curso.siglaestado}`}>
                {curso.nomeintituicao}{curso.sigla_instituicao ? ` (${curso.sigla_instituicao})` : ''} · {curso.nomecidade}/{curso.siglaestado}
              </div>
            </div>
            <div className="cx-col-sit">
              <span className={`cx-selo ${curso.vinculado ? 'cx-vinculado' : 'cx-livre'}`}>{curso.vinculado ? 'Vinculado' : 'Livre'}</span>
            </div>
            <div className={`cx-col-vend cx-vendedor${vendedor === 'A definir' ? ' cx-vazio-txt' : ''}`} title={vendedor}>{vendedor}</div>
            <div className="cx-col-timeline"><Timeline curso={curso} onHistorico={() => onAbrir(curso, { foco: 'historico' })} /></div>
            <div className="cx-col-acao cx-acao">
              <AcaoPrincipal curso={curso} usuario={usuario} permissao={permissao} solicitacoes={solicitacoes} onOrcar={onOrcar} onSolicitar={onSolicitar} onAvaliar={onAvaliar} />
            </div>
            <MenuCurso curso={curso} permissao={permissao} onAbrir={onAbrir} />
          </div>
        );
      })}
      {rodape}
    </div>
  );
}
